import { IconTile } from "@/components/rail-kit/IconTile";
import { RailReveal } from "@/components/rail-kit/RailReveal";
import { cn } from "@/utils";
import { ChevronRight, type LucideIcon } from "lucide-react";
import React, { useState } from "react";
import { RailDisclosureIcon } from "./RailChevron";
import { RailRowOpenButton } from "./RailRowOpenButton";

/** Generic expandable row (folders, connectors, etc.). Whole row toggles open state. */
export function RailExpandableRow({
  icon,
  openIcon,
  label,
  defaultOpen = false,
  dragRef,
  dragProps,
  isDragging,
  children,
}: {
  icon: LucideIcon;
  openIcon?: LucideIcon;
  label: string;
  defaultOpen?: boolean;
  /**
   * Optional dnd-kit drag handle. When set, the header doubles as a drag
   * source (toggle still fires on click — the PointerSensor's 8px
   * activation distance keeps the two from colliding).
   */
  dragRef?: (element: HTMLElement | null) => void;
  dragProps?: React.HTMLAttributes<HTMLElement>;
  isDragging?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="flex flex-col">
      <button
        type="button"
        ref={dragRef}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-state={open ? "open" : "closed"}
        className={cn(
          "flex min-h-7 w-full items-center gap-2.5 rounded-md px-2 text-left hover:bg-n-05",
          isDragging && "opacity-60",
        )}
        {...dragProps}
      >
        <RailDisclosureIcon
          open={open}
          icon={open && openIcon ? openIcon : icon}
        />
        <span className="flex-1 truncate text-base text-n-80">{label}</span>
      </button>
      <RailReveal open={open} contentClassName="flex flex-col pl-2">
        {children}
      </RailReveal>
    </div>
  );
}

/** Workspace row. One gesture set across the rail: single click on the
 *  icon+title expands/collapses the children in place; double click opens the
 *  workspace detail page (`onOpen`). The always-visible chevron button is an
 *  instant toggle from anywhere. */
export function RailWorkspaceExpandableRow({
  icon: TitleIcon,
  iconNode,
  label,
  anchor,
  active,
  trailing,
  menu,
  open: controlledOpen,
  onOpenChange,
  onOpen,
  defaultOpen = false,
  onContextMenu,
  children,
}: {
  icon: LucideIcon;
  /** Optional custom icon (e.g. the workspace's chosen colored icon).
   *  When set it replaces `icon` next to the title. */
  iconNode?: React.ReactNode;
  label: string;
  /** Onboarding target name for the head; its toggle takes `-toggle`. */
  anchor?: string;
  active?: boolean;
  trailing?: React.ReactNode;
  menu?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Navigate to the workspace (title double-click; also in the kebab). */
  onOpen?: () => void;
  defaultOpen?: boolean;
  /** Right-click handler — opens the row's `menu` (when it has one). */
  onContextMenu?: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = controlledOpen ?? uncontrolledOpen;

  const setOpen = (next: boolean) => {
    if (onOpenChange) onOpenChange(next);
    else setUncontrolledOpen(next);
  };
  const toggleOpen = () => setOpen(!open);

  // The title toggles instantly, identical to the chevron — on EVERY row.
  // Deliberate ruling (2026-08-04, supersedes 2026-07-31): double-click-to-
  // open on workspace heads retired, so the 250ms exclusivity window went
  // with it. The hover Open button (and chip) is the one open affordance.

  return (
    <div className="flex flex-col has-[[data-rail-canvas-row]:hover]:[&>[data-workspace-row]]:!bg-transparent">
      <div
        data-workspace-row
        data-onboarding-target={anchor}
        data-state={open ? "open" : "closed"}
        // Same active-row announcement as RailRow — the drawer's auto-open
        // measures against it so expanding never pushes it below the fold.
        aria-current={active ? "page" : undefined}
        onContextMenu={onContextMenu}
        className={cn(
          // `group` (unnamed) as well as `group/row`: the tile's swap keys
          // off row scope, per the rail spec's head anatomy.
          "group group/row relative flex min-h-7 w-full items-center gap-2.5 rounded-md px-2 hover:bg-n-05",
          menu && "pr-8",
          active && "bg-n-05",
        )}
      >
        <button
          type="button"
          onClick={toggleOpen}
          aria-expanded={open}
          aria-label={open ? `Collapse ${label}` : `Expand ${label}`}
          title={open ? "Collapse" : "Expand"}
          data-onboarding-target={anchor ? `${anchor}-toggle` : undefined}
          className="-m-1 inline-flex shrink-0 rounded-sm p-1 hover:bg-n-10"
        >
          {/* The tile carries the swap: glyph at rest, chevron on row
              hover. A workspace's own coloured icon replaces the glyph but
              keeps the coin, so every top-level head anchors identically. */}
          {iconNode ? (
            <span className="relative flex size-5 shrink-0 items-center justify-center rounded-sm bg-n-10 text-n-70">
              <span className="absolute flex size-3 items-center justify-center transition-opacity duration-150 group-hover/row:opacity-0">
                {iconNode}
              </span>
              <ChevronRight
                aria-hidden
                className={cn(
                  "absolute size-3 opacity-0 transition-[opacity,transform] duration-150 ease-out group-hover/row:opacity-100",
                  open && "rotate-90",
                )}
              />
            </span>
          ) : (
            <IconTile icon={TitleIcon} />
          )}
        </button>
        <button
          type="button"
          onClick={toggleOpen}
          aria-expanded={open}
          /**
           * Don't take focus on mousedown. The default would blur whatever the
           * user was in — typically the composer, which autofocuses — so the
           * composer's focus ring would drop on mousedown and spring back on
           * mouseup once navigation remounted and refocused it. That round trip
           * is the flash when opening a workspace from the rail. Suppressing
           * the default keeps focus where it is until the surface actually
           * swaps; `click` still fires on mouseup, and keyboard focus is
           * untouched (Tab and Enter never go through mousedown).
           */
          onMouseDown={(e) => e.preventDefault()}
          className="flex min-w-0 flex-1 select-none items-center self-stretch gap-2.5 text-left"
        >
          {/* Container heads carry more weight than the rows beneath them:
              sans font-medium on n-90, against n-80 regular for contents.
              That contrast is what separates a PLACE you can enter from the
              things inside it — the tile and the typography do the work, so
              the rail needs no rules between sections. */}
          <span className="min-w-0 flex-1 truncate text-base font-medium text-n-90">
            {label}
          </span>
        </button>
        {/* Mirrors the title double-click: open the workspace directly. */}
        {onOpen && <RailRowOpenButton label={label} onOpen={onOpen} />}
        {/* `trailing` lives OUTSIDE the navigation button — it carries
            interactive controls (Upload action, members/share indicator), and
            a <button> can't nest inside the title <button>. */}
        {trailing}
        {menu}
      </div>
      <RailReveal open={open} contentClassName="flex flex-col pl-2">
        {children}
      </RailReveal>
    </div>
  );
}
